import { NavigateFunction } from 'react-router-dom';
import { Question } from 'src/types/helios/Question';
import {
  EditQuestionTranslationCommandInput,
  SubQuestionTranslation,
  TaskDetails,
} from '@amzn/gbsa-helios-typescript-client';
import {
  replacePresignedUrlsWithMediaIdsForQuestion,
  replacePresignedUrlsWithMediaIdsForSubQuestion,
} from 'src/utils/mediaUtils';
import { TranslationFormData } from 'src/validations/translationFormSchema';
import { LANGUAGE_LIST } from 'src/contstant/commonLabels';

/**
 * Returns the display label for a locale code (e.g., 'de-DE' → 'German').
 * Falls back to the raw code if not found in LANGUAGE_LIST.
 */
export const getLanguageLabel = (localeCode: string): string => {
  return LANGUAGE_LIST.find((item) => item.value === localeCode)?.label || localeCode;
};

/**
 * Checks if a specific locale's translation needs review.
 * Uses the `stale` field from the API response (derived from localeTranslationStatusMap)
 * when available, falling back to validFromQuestionVersion comparison for backward
 * compatibility with older data that doesn't have the stale field.
 *
 * @param question - The question to check
 * @param locale - The locale to check (e.g., 'es-ES')
 * @returns true if the translation needs review, false otherwise
 */
export const translationNeedsReview = (
  question: Question | null | undefined,
  locale: string,
): boolean => {
  if (!question) {
    return false;
  }

  const translationContent = question.localeTranslationContentMap?.[locale];
  if (!translationContent) {
    return false;
  }

  // Prefer the stale field from the API response (derived from localeTranslationStatusMap)
  if (translationContent.stale !== undefined && translationContent.stale !== null) {
    return translationContent.stale;
  }

  // Fallback: compare validFromQuestionVersion for backward compatibility
  if (!translationContent.validFromQuestionVersion) {
    return false;
  }

  return Number(translationContent.validFromQuestionVersion) < Number(question.version);
};

/** Type alias for subquestion input from form data */
type TranslationSubQuestionInput = NonNullable<
  TranslationFormData['subQuestions']
>[number];

/**
 * Determines if a user can edit a translation for a given question and language
 *
 * @param question - The question object to check
 * @param selectedLanguage - The language the user wants to edit
 * @param enableTranslationEdit - Feature flag to enable/disable translation editing
 * @returns true if the user can edit the translation, false otherwise
 *
 * Edit is allowed when:
 * - Language is the question's default language (always editable), OR
 * - enableTranslationEdit is true (allows editing/adding translations for any non-default language)
 */
export const canEditTranslation = (
  question: Question | null | undefined,
  selectedLanguage: string,
  enableTranslationEdit: boolean,
): boolean => {
  if (!question) {
    return false;
  }

  // Question's default language is always editable
  if (selectedLanguage === question.defaultLanguage) {
    return true;
  }

  // Non-default languages require feature flag enabled — no longer requires existing translation content
  return enableTranslationEdit;
};

/**
 * Checks if a translation already exists for a given question and locale.
 *
 * @param question - The question to check
 * @param locale - The locale to check (e.g., 'es-ES')
 * @returns true if translation content exists for the locale, false otherwise
 */
export const hasExistingTranslation = (
  question: Question | null | undefined,
  locale: string,
): boolean => {
  return !!question?.localeTranslationContentMap?.[locale];
};

/**
 * Checks if the current edit operation is for a translation (non-default language)
 *
 * @param selectedLanguage - The language being edited
 * @param defaultLanguage - The default language of the question
 * @returns true if editing a translation, false if editing default language
 */
export const isEditingTranslation = (
  selectedLanguage: string | undefined,
  defaultLanguage: string | undefined,
): boolean => {
  // Both must be defined and different for it to be a translation edit
  if (!selectedLanguage || !defaultLanguage) {
    return false;
  }

  return selectedLanguage !== defaultLanguage;
};

/**
 * Transforms form data into translation API request format
 * Extracts translation-specific data and maps subquestions by subQuestionId
 * Handles replacing presigned URLs with media IDs for all fields
 *
 * @param question - The original question being translated (contains media responses)
 * @param formData - The form data with updated translations (may contain presigned URLs)
 * @param subQuestions - Subquestions array (may contain presigned URLs)
 * @param taskDetails - Task details for the translation (may contain presigned URLs)
 * @param userId - ID of the user making the update
 * @param programId - Program ID
 * @param businessUnit - Business unit ID
 * @param language - Target language code
 * @returns Formatted translation update request with media IDs replacing presigned URLs
 *
 * @example
 * ```typescript
 * const request = buildTranslationRequest(
 *   question,
 *   formData,
 *   subQuestions,
 *   taskDetails,
 *   'user1',
 *   'PROG1',
 *   'BU1',
 *   'es'
 * );
 * updateTranslation(request);
 * ```
 */
export const buildTranslationRequest = (
  question: Question,
  formData: {
    title: string;
    questionText: string;
    auditInstruction: string;
    passCriteria: string;
    failCriteria: string;
    justification: string;
  },
  subQuestions: TranslationSubQuestionInput[] | undefined,
  taskDetails: TaskDetails | undefined,
  userId: string,
  programId: string,
  businessUnit: string,
  language: string,
): EditQuestionTranslationCommandInput => {
  // Replace presigned URLs with media IDs for main question fields
  const processedQuestionDetails = {
    title: formData.title,
    questionText: replacePresignedUrlsWithMediaIdsForQuestion(
      formData.questionText,
      question.mediaResponses,
    ),
    auditInstruction: replacePresignedUrlsWithMediaIdsForQuestion(
      formData.auditInstruction,
      question.mediaResponses,
    ),
    passCriteria: replacePresignedUrlsWithMediaIdsForQuestion(
      formData.passCriteria,
      question.mediaResponses,
    ),
    failCriteria: replacePresignedUrlsWithMediaIdsForQuestion(
      formData.failCriteria,
      question.mediaResponses,
    ),
    justification: replacePresignedUrlsWithMediaIdsForQuestion(
      formData.justification,
      question.mediaResponses,
    ),
  };

  // Process task details if present
  let processedTaskDetails: TaskDetails | undefined;
  if (taskDetails) {
    processedTaskDetails = {
      taskName: taskDetails.taskName,
      taskDescription: replacePresignedUrlsWithMediaIdsForQuestion(
        taskDetails.taskDescription,
        question.mediaResponses,
      ),
    };
  }

  // Build subquestion translations map keyed by subQuestionId
  // Replace presigned URLs with media IDs for each subquestion
  const subQuestionTranslations: Record<string, SubQuestionTranslation> = {};

  subQuestions?.forEach((sq, idx) => {
    const originalSubQuestion = question.subQuestions?.[idx];
    if (!originalSubQuestion?.subQuestionId) return;

    // Get media responses for this subquestion's fields
    const instructionMediaResponses = originalSubQuestion.mediaResponsesMap?.instruction;
    const taskDescriptionMediaResponses =
      originalSubQuestion.mediaResponsesMap?.taskDescription;

    // Replace URLs with media IDs for instruction
    const processedInstruction = replacePresignedUrlsWithMediaIdsForSubQuestion(
      sq.instruction,
      instructionMediaResponses,
    );

    // Process task details if present
    let processedSubTaskDetails: TaskDetails | undefined;
    if (sq.taskDetails && sq.taskDetails.taskName && sq.taskDetails.taskDescription) {
      const processedTaskDescription = replacePresignedUrlsWithMediaIdsForSubQuestion(
        sq.taskDetails.taskDescription,
        taskDescriptionMediaResponses,
      );

      processedSubTaskDetails = {
        taskName: sq.taskDetails.taskName,
        taskDescription: processedTaskDescription,
      };
    }

    subQuestionTranslations[originalSubQuestion.subQuestionId] = {
      instruction: processedInstruction,
      title: sq.title,
      taskDetails: processedSubTaskDetails,
    };
  });

  return {
    programId,
    businessUnit,
    divisionName: question.divisionName,
    questionId: question.questionId,
    language,
    questionTranslation: {
      questionDetails: processedQuestionDetails,
      taskDetails: processedTaskDetails,
    },
    subQuestionTranslations,
    updatedBy: userId,
  };
};

/**
 * Navigates to the translation edit page with query parameters
 * @param navigate - React Router navigation function
 * @param data - Question data to edit
 * @param selectedLanguage - Language to edit
 */
export function openTranslationEditPage(
  navigate: NavigateFunction,
  data: Question,
  selectedLanguage: string,
) {
  // Build URL with query parameters (consistent with question-details)
  const url = `/question/translation-edit?questionId=${data.questionId}&programId=${data.program}&division=${data.divisionName}&businessUnit=${data.businessUnit}`;
  // Pass question data and selected language through navigation state
  navigate(url, {
    state: {
      question: data,
      selectedLanguage: selectedLanguage,
    },
  });
}

/**
 * Decodes HTML entities in a string
 * @param html - String with HTML entities
 * @returns String with decoded entities
 */
export const decodeHtmlEntities = (html: string): string => {
  if (!html) return '';
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
};

/**
 * Extracts and normalizes image URLs from HTML string
 * @param html - HTML string containing img tags
 * @returns Sorted array of decoded image URLs
 */
export const extractImageUrls = (html: string): string[] => {
  if (!html) return [];

  const imgRegex = /<img[^>]+src="([^">]+)"/g;
  const urls: string[] = [];
  let match;

  while ((match = imgRegex.exec(html)) !== null) {
    const decodedUrl = decodeHtmlEntities(match[1]);
    urls.push(decodedUrl);
  }

  return urls.sort();
};

/**
 * Checks if images have changed between two HTML strings
 * @param original - Original HTML string
 * @param current - Current HTML string
 * @returns true if images differ, false otherwise
 */
export const hasImagesChanged = (
  original: string | undefined,
  current: string | undefined,
): boolean => {
  const originalImages = extractImageUrls(original || '');
  const currentImages = extractImageUrls(current || '');

  if (originalImages.length !== currentImages.length) {
    return true;
  }

  return originalImages.some((url, index) => url !== currentImages[index]);
};
